/**
 * ReturnedSchedulesTab.js
 *
 * Displays TransactionSchedule rows where at least one allocation has been
 * returned (RETURNED_PARTIAL or RETURNED_FULL check status).
 *
 * Usage in TransactionSchedulePage:
 *   import ReturnedSchedulesTab from "../components/transactionSchedule/ReturnedSchedulesTab";
 *
 *   // In the Tabs items array:
 *   {
 *     key: "returned-schedules",
 *     label: (
 *       <span style={{ color: returnedSchedules.length > 0 ? "#d46b08" : undefined, fontWeight: 600 }}>
 *         Буцаалттай хуваариуд ({returnedSchedules.length})
 *       </span>
 *     ),
 *     children: (
 *       <ReturnedSchedulesTab
 *         returnedSchedules={returnedSchedules}
 *         loadingReturnedSchedules={loadingReturnedSchedules}
 *         openManage={openManage}
 *         openDetails={openDetails}
 *       />
 *     ),
 *   }
 */

import React, { useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Descriptions,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import { EditOutlined, EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import {
    formatAmount,
    formatDateTime,
    getCheckStatusColor,
    getCheckStatusLabel,
    addTextColumnTools,
    addNumberColumnTools,
    addDateColumnTools,
} from "./transactionScheduleUi";
import TableMetaBar from "../common/TableMetaBar";

const { Text } = Typography;

function renderCompactText(value, maxWidth = 200) {
    const text = value || "-";
    return (
        <Tooltip title={text}>
            <div
                style={{
                    maxWidth,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {text}
            </div>
        </Tooltip>
    );
}

/**
 * Returns a human-readable label for the check status combination
 * present on a returned schedule row.
 */
function ReturnStatusSummary({ record }) {
    const statuses = record.checkSummaryStatuses || [];
    const returnStatuses = statuses.filter(
        (s) => s === "RETURNED_PARTIAL" || s === "RETURNED_FULL" || s === "RETURN_RESET"
    );
    const otherStatuses = statuses.filter(
        (s) => s !== "RETURNED_PARTIAL" && s !== "RETURNED_FULL" && s !== "RETURN_RESET"
    );

    return (
        <Space size={[4, 4]} wrap>
            {returnStatuses.map((s) => (
                <Tag key={s} color={getCheckStatusColor(s)}>
                    {getCheckStatusLabel(s)}
                </Tag>
            ))}
            {otherStatuses.slice(0, 2).map((s) => (
                <Tag key={s} color={getCheckStatusColor(s)}>
                    {getCheckStatusLabel(s)}
                </Tag>
            ))}
            {otherStatuses.length > 2 && (
                <Tag>+{otherStatuses.length - 2}</Tag>
            )}
        </Space>
    );
}

function ReturnedSchedulesTab({
    returnedSchedules = [],
    loadingReturnedSchedules = false,
    openManage,
    openDetails,
    onRefresh,
}) {
    const [tableMeta, setTableMeta] = useState({
        filteredCount: null,
        filters: {},
        sorter: {},
    });

    const handleTableChange = (_, filters, sorter, extra) => {
        setTableMeta({
            filteredCount: Array.isArray(extra?.currentDataSource)
                ? extra.currentDataSource.length
                : null,
            filters: filters || {},
            sorter: sorter || {},
        });
    };

    // Sort: fully returned first, then partial; within each group by updatedAt desc
    const sorted = useMemo(() => {
        return [...returnedSchedules].sort((a, b) => {
            const aFull = (a.checkSummaryStatuses || []).includes("RETURNED_FULL");
            const bFull = (b.checkSummaryStatuses || []).includes("RETURNED_FULL");
            if (aFull && !bFull) return -1;
            if (!aFull && bFull) return 1;
            const aReset = (a.checkSummaryStatuses || []).includes("RETURN_RESET");
            const bReset = (b.checkSummaryStatuses || []).includes("RETURN_RESET");
            if (aReset && !bReset) return -1;
            if (!aReset && bReset) return 1;
            const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            return bTime - aTime;
        });
    }, [returnedSchedules]);

    // Summary counts
    const fullCount = sorted.filter((r) =>
        (r.checkSummaryStatuses || []).includes("RETURNED_FULL")
    ).length;
    const partialCount = sorted.filter(
        (r) =>
            (r.checkSummaryStatuses || []).includes("RETURNED_PARTIAL") &&
            !(r.checkSummaryStatuses || []).includes("RETURNED_FULL")
    ).length;
    const resetCount = sorted.filter(
        (r) =>
            (r.checkSummaryStatuses || []).includes("RETURN_RESET") &&
            !(r.checkSummaryStatuses || []).includes("RETURNED_PARTIAL") &&
            !(r.checkSummaryStatuses || []).includes("RETURNED_FULL")
    ).length;

    const columns = useMemo(() => {
        const base = [
            {
                title: "ID",
                dataIndex: "id",
                key: "id",
                width: 70,
                fixed: "left",
            },
            {
                title: "Хүсэлт",
                key: "requestInfo",
                width: 190,
                fixed: "left",
                render: (_, record) => (
                    <div>
                        <div style={{ fontWeight: 600 }}>
                            {record.requestNumber || `#${record.id}`}
                        </div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                            {formatDateTime(record.createdAt)}
                        </div>
                    </div>
                ),
            },
            {
                title: "Төлөгч",
                dataIndex: "payer",
                key: "payer",
                width: 210,
                render: (value) => renderCompactText(value, 190),
            },
            {
                title: "Хүлээн авагч",
                dataIndex: "beneficiaryCompany",
                key: "beneficiaryCompany",
                width: 200,
                render: (value) => renderCompactText(value, 180),
            },
            {
                title: "Дүн",
                dataIndex: "paymentAmount",
                key: "paymentAmount",
                width: 130,
                render: (value, record) => (
                    <div>
                        <div style={{ fontWeight: 600 }}>{formatAmount(value)}</div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                            {record.currency || "-"}
                        </div>
                    </div>
                ),
            },
            {
                title: "Буцаалтын төлөв",
                key: "returnStatus",
                width: 260,
                render: (_, record) => <ReturnStatusSummary record={record} />,
            },
            {
                title: "Буцаасан хуваарилалт",
                key: "returnedCount",
                width: 160,
                render: (_, record) => {
                    const total = Number(record.allocationCount || 0);
                    const returned = Number(record.returnedAllocationCount || 0);
                    return (
                        <div>
                            <div style={{ fontWeight: 600, color: "#d46b08" }}>
                                {returned} / {total}
                            </div>
                            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                                хуваарилалт буцсан
                            </div>
                        </div>
                    );
                },
            },
            {
                title: "Зөвшөөрөл",
                key: "agreeStatus",
                width: 130,
                render: (_, record) => {
                    const total = Number(record.allocationCount || 0);
                    const agreed = Number(record.agreedAllocationCount || 0);
                    const done = total > 0 && total === agreed;
                    return (
                        <div>
                            <div style={{ fontWeight: 600 }}>
                                {agreed}/{total}
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    color: done ? "#389e0d" : "#8c8c8c",
                                }}
                            >
                                {done ? "Бүрэн" : "Дутуу"}
                            </div>
                        </div>
                    );
                },
            },
            {
                title: "Шинэчилсэн",
                dataIndex: "updatedAt",
                key: "updatedAt",
                width: 150,
                render: (value) => (
                    <div style={{ fontSize: 12, color: "#595959" }}>
                        {formatDateTime(value)}
                    </div>
                ),
            },
            {
                title: "Үйлдэл",
                key: "actions",
                width: 110,
                fixed: "right",
                render: (_, record) => (
                    <Space size={6}>
                        <Tooltip title="Хуваарь удирдах">
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => openManage?.(record)}
                            />
                        </Tooltip>
                        <Tooltip title="Дэлгэрэнгүй">
                            <Button
                                icon={<EyeOutlined />}
                                onClick={() => openDetails?.(record)}
                            />
                        </Tooltip>
                    </Space>
                ),
            },
        ];

        return base.map((col) => {
            if (
                ["requestInfo", "actions", "agreeStatus", "returnStatus", "returnedCount"].includes(
                    col.key
                )
            ) {
                return col;
            }
            if (col.dataIndex === "payer") {
                return addTextColumnTools(col, returnedSchedules, "payer");
            }
            if (col.dataIndex === "beneficiaryCompany") {
                return addTextColumnTools(col, returnedSchedules, "beneficiaryCompany");
            }
            if (col.dataIndex === "paymentAmount") {
                return addNumberColumnTools(col);
            }
            if (col.dataIndex === "updatedAt") {
                return addDateColumnTools(col, "updatedAt", "descend");
            }
            return col;
        });
    }, [returnedSchedules, openManage, openDetails]);

    return (
        <Card
            title={
                <Space>
                    <span>Буцаалттай хуваариуд</span>
                    <Badge
                        count={sorted.length}
                        style={{ backgroundColor: sorted.length > 0 ? "#fa8c16" : "#d9d9d9" }}
                    />
                </Space>
            }
            extra={
                onRefresh ? (
                    <Tooltip title="Дахин ачаалах">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={onRefresh}
                            loading={loadingReturnedSchedules}
                        />
                    </Tooltip>
                ) : null
            }
        >
            <Alert
                type="warning"
                showIcon
                message="Буцаалттай гүйлгээний хуваариуд"
                description="Доорх хуваариудын хуваарилалтуудад буцаалт бүртгэгдсэн байна. Хуваарилалтыг нягталж, шаардлагатай бол дахин боловсруулна уу."
                style={{ marginBottom: 16 }}
            />

            {/* Summary badges */}
            <Space style={{ marginBottom: 16 }} wrap>
                <Tag color="red" style={{ fontSize: 13, padding: "2px 10px" }}>
                    Бүтэн буцсан: {fullCount}
                </Tag>
                <Tag color="volcano" style={{ fontSize: 13, padding: "2px 10px" }}>
                    Хэсэгчлэн буцсан: {partialCount}
                </Tag>
                {resetCount > 0 && (
                    <Tag color="magenta" style={{ fontSize: 13, padding: "2px 10px" }}>
                        Дахин бөглөх шаардлагатай: {resetCount}
                    </Tag>
                )}
                <Tag color="default" style={{ fontSize: 13, padding: "2px 10px" }}>
                    Нийт: {sorted.length}
                </Tag>
            </Space>

            <TableMetaBar
                total={sorted.length}
                filtered={tableMeta.filteredCount}
                filters={tableMeta.filters}
                sorter={tableMeta.sorter}
            />

            <Table
                rowKey="id"
                loading={loadingReturnedSchedules}
                columns={columns}
                dataSource={sorted}
                onChange={handleTableChange}
                scroll={{ x: 1400 }}
                size="middle"
                rowClassName={(record) => {
                    const statuses = record.checkSummaryStatuses || [];
                    if (statuses.includes("RETURNED_FULL")) return "ts-row-returned";
                    if (statuses.includes("RETURNED_PARTIAL")) return "ts-row-review";
                    if (statuses.includes("RETURN_RESET")) return "ts-row-reset";
                    return "";
                }}
                pagination={{
                    showSizeChanger: true,
                    defaultPageSize: 20,
                    pageSizeOptions: [10, 20, 50, 100],
                }}
                expandable={{
                    expandedRowRender: (record) => (
                        <Descriptions
                            bordered
                            size="small"
                            column={{ xs: 1, sm: 2, md: 3 }}
                        >
                            <Descriptions.Item label="Хүлээн авагч байгууллага">
                                {record.beneficiaryCompany || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Хүлээн авагч банк">
                                {record.beneficiaryBank || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Хүлээн авагч данс">
                                {record.beneficiaryAccount || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Гүйлгээний утга" span={3}>
                                <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                    {record.paymentPurpose || "-"}
                                </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="Нийт хуваарилалт">
                                {record.allocationCount ?? "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Зөвшөөрөгдсөн">
                                {record.agreedAllocationCount ?? 0}
                            </Descriptions.Item>
                            <Descriptions.Item label="Буцсан хуваарилалт">
                                <Text type="warning" strong>
                                    {record.returnedAllocationCount ?? 0}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Хуваарилсан нийт дүн">
                                {formatAmount(record.allocatedAmountTotal)}{" "}
                                {record.currency || ""}
                            </Descriptions.Item>
                            <Descriptions.Item label="Үлдэгдэл дүн">
                                {formatAmount(record.remainingAmount)}{" "}
                                {record.currency || ""}
                            </Descriptions.Item>
                            <Descriptions.Item label="Эх сурвалж">
                                {record.sourceType || "-"}
                            </Descriptions.Item>
                            {record.checkSummaryStatuses?.length > 0 && (
                                <Descriptions.Item label="Шалгалтын бүх төлөв" span={3}>
                                    <Space wrap>
                                        {record.checkSummaryStatuses.map((s) => (
                                            <Tag key={s} color={getCheckStatusColor(s)}>
                                                {getCheckStatusLabel(s)}
                                            </Tag>
                                        ))}
                                    </Space>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    ),
                    rowExpandable: () => true,
                }}
            />
        </Card>
    );
}

export default ReturnedSchedulesTab;